import { z } from 'zod';

// Validation messages — match the i18n keys under
// `inventories.edit.default_inventory.validation.*`. Default values here are
// English fallbacks; real strings come from `t()` at the React call site via
// `makeUpdateInventoryFormSchema`.
export interface InventoryConfigurationValidationMessages {
  nameRequired?: string;
  quantityRequired?: string;
  unitsRequired?: string;
  scalesWithRequired?: string;
  formatRequired?: string;
  multiplierRequired?: string;
}

const DEFAULT_VALIDATION_MESSAGES: Required<InventoryConfigurationValidationMessages> = {
  nameRequired: 'Name is required',
  quantityRequired: 'Quantity is required',
  unitsRequired: 'Units is required',
  scalesWithRequired: 'Scales with is required',
  formatRequired: 'Format is required',
  multiplierRequired: 'Multiplier is required when Scales with is Multiplier',
};

function makeInventoryConfigurationSchema(messages: Required<InventoryConfigurationValidationMessages>) {
  return z.object({
    id: z.number().optional(),
    // Stable client-only id assigned when a brand-new entry is pushed. Used as
    // the React key so mid-list deletions don't alias surviving cards to
    // different component instances. Never sent to the backend (handleSubmit
    // doesn't include it in the payload).
    _tempId: z.string().optional(),
    quantity: z.number().nullable().optional(),
    value: z.number().nullable().optional(),
    default_type: z.string().nullable().optional(),
    default_event_type: z.string().nullable().optional(),
    multiplier: z.string().nullable().optional(),
  })
    .refine((c) => c.quantity != null, { path: ['quantity'], message: messages.quantityRequired })
    .refine((c) => c.value != null, { path: ['value'], message: messages.unitsRequired })
    .refine((c) => !!c.default_type, { path: ['default_type'], message: messages.scalesWithRequired })
    .refine((c) => !!c.default_event_type, { path: ['default_event_type'], message: messages.formatRequired })
    .refine(
      (c) => c.default_type !== 'Computed' || !!c.multiplier,
      { path: ['multiplier'], message: messages.multiplierRequired },
    );
}

/**
 * Build the Edit Inventory form schema with i18n-resolved validation messages.
 * Pass in translated strings from `useTranslation()` so error messages respect
 * the locale instead of the English fallbacks baked in here.
 */
export function makeUpdateInventoryFormSchema(messages?: InventoryConfigurationValidationMessages) {
  const m = { ...DEFAULT_VALIDATION_MESSAGES, ...messages };
  return z.object({
    name: z.string().min(1, m.nameRequired),
    category: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    recommended_retail_price: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? null : Number(val)),
      z.number().min(0).nullable().optional()
    ),
    cost_to_company: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? null : Number(val)),
      z.number().min(0).nullable().optional()
    ),
    inventory_configurations: z.array(makeInventoryConfigurationSchema(m)).optional(),
    // Bumped when images are added/removed so the form's isDirty flag captures image changes.
    _imageVersion: z.number().default(0),
  });
}

// Static fallback schema for tests and other callers that don't need i18n.
export const inventoryConfigurationSchema = makeInventoryConfigurationSchema(DEFAULT_VALIDATION_MESSAGES);
export const updateInventoryFormSchema = makeUpdateInventoryFormSchema();

export type InventoryConfigurationFormData = z.infer<typeof inventoryConfigurationSchema>;
export type UpdateInventoryFormData = z.infer<typeof updateInventoryFormSchema>;

export const createInventoryFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.string().min(1, 'Category is required'),
  location: z.string().min(1, 'Location is required'),
  recommended_retail_price: z.preprocess(
    (val) => (val === '' || val === undefined || val === null ? null : Number(val)),
    z.number().min(0).nullable().optional()
  ),
  cost_to_company: z.preprocess(
    (val) => (val === '' || val === undefined || val === null ? null : Number(val)),
    z.number().min(0).nullable().optional()
  ),
});

export type CreateInventoryFormData = z.infer<typeof createInventoryFormSchema>;
